import { AlertCircle } from 'lucide-react';
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn';

const campoBase = [
  'w-full bg-[var(--cor-superficie)] text-[var(--cor-texto)]',
  'border border-[var(--cor-borda-forte)] rounded-[var(--radius-md)]',
  'px-3 h-11 min-h-11 text-base sm:text-sm',
  'placeholder:text-[var(--cor-texto-fraco)]',
  'transition-[border-color,box-shadow] duration-150',
  'focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 focus:outline-none',
  'disabled:bg-[var(--cor-superficie-2)] disabled:text-[var(--cor-texto-suave)]',
];

export interface FieldProps {
  label: string;
  /** Explicação curta, acima do campo: ensina antes de o erro acontecer. */
  ajuda?: string;
  erro?: string | undefined;
  obrigatorio?: boolean;
  children: (ids: { id: string; descriptionId: string; invalido: boolean }) => ReactNode;
}

/** Rótulo, ajuda e erro sempre associados ao campo por id (WCAG 2.2 AA). */
export function Field({ label, ajuda, erro, obrigatorio, children }: FieldProps) {
  const id = useId();
  const descriptionId = `${id}-descricao`;

  return (
    <div className="flex flex-col gap-1.5">
      {/* O asterisco fica FORA do <label>: dentro dele, viraria parte do nome
          do campo para leitores de tela e para testes automatizados. */}
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="text-sm font-medium text-[var(--cor-texto)]">
          {label}
        </label>
        {obrigatorio ? (
          <span className="text-[var(--status-recusado)]" aria-hidden>
            *
          </span>
        ) : null}
      </div>

      {ajuda && !erro ? (
        <p id={descriptionId} className="text-xs text-[var(--cor-texto-suave)]">
          {ajuda}
        </p>
      ) : null}

      {children({ id, descriptionId, invalido: Boolean(erro) })}

      {erro ? (
        <p
          id={descriptionId}
          role="alert"
          className="flex items-start gap-1.5 text-xs font-medium text-[var(--status-recusado)]"
        >
          <AlertCircle aria-hidden className="mt-px size-3.5 shrink-0" />
          {erro}
        </p>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalido?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { className, invalido, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalido || undefined}
      className={cn(
        campoBase,
        invalido && 'border-[var(--status-recusado)] focus:border-[var(--status-recusado)] focus:ring-[var(--status-recusado)]/20',
        className,
      )}
      {...props}
    />
  );
});

export interface SelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalido?: boolean;
}

export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(function SelectInput(
  { className, invalido, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalido || undefined}
      className={cn(campoBase, 'appearance-none pr-9', className)}
      {...props}
    >
      {children}
    </select>
  );
});
